import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import type { QuizQuestionInput, QuizQuestionKind } from '../../../lib/api/assignment.api';

// The closed (auto-graded) kinds plus `open` (Delivery 2): AI proposes a score
// against the teacher's standard and the teacher decides.
const BUILDER_KINDS: QuizQuestionKind[] = [
  'single_choice',
  'multiple_choice',
  'matching',
  'short_answer',
  'ordering',
  'numeric',
  'cloze',
  'open',
];

const DEFAULT_NORM = { trim: true, lowercase: true, stripAccents: true };

function newId(): string {
  return crypto.randomUUID();
}

/** Blank ids referenced by a cloze template, e.g. "En el [[1]]…" → ["1"]. */
function parseBlankIds(template: string): string[] {
  const ids: string[] = [];
  const re = /\[\[(\d+)\]\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(template)) !== null) if (!ids.includes(m[1]!)) ids.push(m[1]!);
  return ids;
}

function blankQuestion(kind: QuizQuestionKind): QuizQuestionInput {
  const base = { id: newId(), prompt: '', points: 1 };
  switch (kind) {
    case 'single_choice':
      return {
        ...base,
        kind,
        options: [
          { id: newId(), text: '' },
          { id: newId(), text: '' },
        ],
        correctOptionId: '',
      };
    case 'multiple_choice':
      return {
        ...base,
        kind,
        options: [
          { id: newId(), text: '' },
          { id: newId(), text: '' },
        ],
        correctOptionIds: [],
      };
    case 'matching': {
      const l0 = newId();
      const r0 = newId();
      // The correct match is the same-row pair (left ↔ right on the row). The
      // student sees both columns shuffled; grading uses the ids, not the order.
      return {
        ...base,
        kind,
        left: [{ id: l0, text: '' }],
        right: [{ id: r0, text: '' }],
        correctPairs: [{ leftId: l0, rightId: r0 }],
      };
    }
    case 'short_answer':
      return { ...base, kind, acceptedAnswers: [''], normalization: DEFAULT_NORM };
    case 'ordering': {
      const items = [
        { id: newId(), text: '' },
        { id: newId(), text: '' },
      ];
      return { ...base, kind, items, correctOrder: items.map((i) => i.id) };
    }
    case 'numeric':
      return { ...base, kind, value: 0, tolerance: 0 };
    case 'cloze':
      return { ...base, kind, template: '', blanks: {} };
    case 'open':
      return { ...base, kind, standard: '' };
  }
}

export function QuizQuestionsEditor({
  value,
  onChange,
}: {
  value: QuizQuestionInput[];
  onChange: (next: QuizQuestionInput[]) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');

  const update = (idx: number, patch: Partial<QuizQuestionInput>) => {
    onChange(value.map((q, i) => (i === idx ? ({ ...q, ...patch } as QuizQuestionInput) : q)));
  };
  const addQuestion = (kind: QuizQuestionKind) => {
    onChange([...value, blankQuestion(kind)]);
  };
  const removeQuestion = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };
  const moveQuestion = (idx: number, to: number) => {
    if (to < 0 || to >= value.length) return;
    const next = [...value];
    const [moved] = next.splice(idx, 1);
    next.splice(to, 0, moved!);
    onChange(next);
  };

  const totalPoints = value.reduce((sum, q) => sum + (q.points || 0), 0);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">{t('assignmentsAdmin.quiz.questionsTitle')}</Typography>
        <Typography variant="caption" color="text.secondary">
          {t('assignmentsAdmin.quiz.totalPoints', { count: totalPoints })}
        </Typography>
      </Stack>

      {value.map((q, idx) => (
        <Box key={q.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
              {t('assignmentsAdmin.quiz.questionN', { n: idx + 1 })} ·{' '}
              {t(`assignmentsAdmin.quiz.kind.${q.kind}`)}
            </Typography>
            <TextField
              size="small"
              type="number"
              label={t('assignmentsAdmin.quiz.points')}
              value={q.points}
              onChange={(e) => update(idx, { points: Number(e.target.value) })}
              sx={{ width: 90 }}
            />
            <IconButton
              size="small"
              onClick={() => moveQuestion(idx, idx - 1)}
              disabled={idx === 0}
              aria-label={t('assignmentsAdmin.quiz.moveQuestionUp')}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => moveQuestion(idx, idx + 1)}
              disabled={idx === value.length - 1}
              aria-label={t('assignmentsAdmin.quiz.moveQuestionDown')}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => removeQuestion(idx)}
              aria-label={t('assignmentsAdmin.quiz.removeQuestion')}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>

          <TextField
            fullWidth
            size="small"
            label={t('assignmentsAdmin.quiz.prompt')}
            value={typeof q.prompt === 'string' ? q.prompt : ''}
            onChange={(e) => update(idx, { prompt: e.target.value })}
            sx={{ mb: 1.5 }}
          />

          <QuestionBody question={q} index={idx} update={update} />
        </Box>
      ))}

      <Divider />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {BUILDER_KINDS.map((k) => (
          <Button
            key={k}
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => addQuestion(k)}
          >
            {t(`assignmentsAdmin.quiz.kind.${k}`)}
          </Button>
        ))}
      </Stack>
    </Stack>
  );
}

function QuestionBody({
  question,
  index,
  update,
}: {
  question: QuizQuestionInput;
  index: number;
  update: (idx: number, patch: Partial<QuizQuestionInput>) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');

  if (question.kind === 'single_choice' || question.kind === 'multiple_choice') {
    const options = question.options;
    const setOptions = (next: typeof options) => update(index, { options: next });
    const isMulti = question.kind === 'multiple_choice';
    const correctSet = isMulti
      ? new Set(question.correctOptionIds)
      : new Set(question.correctOptionId ? [question.correctOptionId] : []);

    const toggleCorrect = (optId: string) => {
      if (isMulti) {
        const next = new Set(correctSet);
        if (next.has(optId)) next.delete(optId);
        else next.add(optId);
        update(index, { correctOptionIds: [...next] });
      } else {
        update(index, { correctOptionId: optId });
      }
    };

    return (
      <Stack spacing={1}>
        <Typography variant="caption" color="text.secondary">
          {t('assignmentsAdmin.quiz.markCorrect')}
        </Typography>
        {options.map((opt) => (
          <Stack key={opt.id} direction="row" alignItems="center" spacing={1}>
            {isMulti ? (
              <Checkbox
                size="small"
                checked={correctSet.has(opt.id)}
                onChange={() => toggleCorrect(opt.id)}
              />
            ) : (
              <Radio
                size="small"
                checked={correctSet.has(opt.id)}
                onChange={() => toggleCorrect(opt.id)}
              />
            )}
            <TextField
              fullWidth
              size="small"
              placeholder={t('assignmentsAdmin.quiz.optionText')}
              value={opt.text}
              onChange={(e) =>
                setOptions(
                  options.map((o) => (o.id === opt.id ? { ...o, text: e.target.value } : o)),
                )
              }
            />
            <IconButton
              size="small"
              disabled={options.length <= 2}
              onClick={() => setOptions(options.filter((o) => o.id !== opt.id))}
              aria-label={t('assignmentsAdmin.quiz.removeOption')}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setOptions([...options, { id: newId(), text: '' }])}
        >
          {t('assignmentsAdmin.quiz.addOption')}
        </Button>
        {isMulti && (
          <FormControl size="small" sx={{ maxWidth: 320 }}>
            <InputLabel>{t('assignmentsAdmin.quiz.questionPartialCredit')}</InputLabel>
            <Select
              label={t('assignmentsAdmin.quiz.questionPartialCredit')}
              value={question.partialCreditPolicy ?? 'all-or-nothing'}
              onChange={(e) =>
                update(index, {
                  partialCreditPolicy: e.target.value as 'all-or-nothing' | 'proportional',
                })
              }
            >
              <MenuItem value="all-or-nothing">
                {t('assignmentsAdmin.quiz.policyAllOrNothing')}
              </MenuItem>
              <MenuItem value="proportional">
                {t('assignmentsAdmin.quiz.policyProportional')}
              </MenuItem>
            </Select>
          </FormControl>
        )}
      </Stack>
    );
  }

  if (question.kind === 'matching') {
    const { left, right } = question;
    // The correct match is the same-row pair. Keep correctPairs in lockstep with
    // the rows so the teacher never has to pick it manually. Grading is by id, and
    // the student sees both columns shuffled — so same-row is not a giveaway.
    const pairsForRows = (l: typeof left, r: typeof right) =>
      l.map((li, i) => ({ leftId: li.id, rightId: r[i]!.id })).filter((_, i) => !!r[i]);
    const setLeftText = (li: number, text: string) => {
      update(index, { left: left.map((x, i) => (i === li ? { ...x, text } : x)) });
    };
    const setRightText = (li: number, text: string) => {
      update(index, { right: right.map((x, i) => (i === li ? { ...x, text } : x)) });
    };
    const addRow = () => {
      const nextLeft = [...left, { id: newId(), text: '' }];
      const nextRight = [...right, { id: newId(), text: '' }];
      update(index, {
        left: nextLeft,
        right: nextRight,
        correctPairs: pairsForRows(nextLeft, nextRight),
      });
    };
    const removeRow = (li: number) => {
      const nextLeft = left.filter((_, i) => i !== li);
      const nextRight = right.filter((_, i) => i !== li);
      update(index, {
        left: nextLeft,
        right: nextRight,
        correctPairs: pairsForRows(nextLeft, nextRight),
      });
    };
    return (
      <Stack spacing={1}>
        <Typography variant="caption" color="text.secondary">
          {t('assignmentsAdmin.quiz.matchingHelp')}
        </Typography>
        {left.map((l, li) => (
          <Stack key={l.id} direction="row" alignItems="center" spacing={1}>
            <TextField
              size="small"
              placeholder={t('assignmentsAdmin.quiz.leftItem')}
              value={l.text}
              onChange={(e) => setLeftText(li, e.target.value)}
              sx={{ flex: 1 }}
            />
            <Typography color="text.secondary" sx={{ flexShrink: 0 }}>
              →
            </Typography>
            <TextField
              size="small"
              placeholder={t('assignmentsAdmin.quiz.rightItem')}
              value={right[li]?.text ?? ''}
              onChange={(e) => setRightText(li, e.target.value)}
              sx={{ flex: 1 }}
            />
            <IconButton
              size="small"
              disabled={left.length <= 1}
              onClick={() => removeRow(li)}
              aria-label={t('assignmentsAdmin.quiz.removeOption')}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={addRow}>
          {t('assignmentsAdmin.quiz.addPair')}
        </Button>
      </Stack>
    );
  }

  if (question.kind === 'ordering') {
    const items = question.items;
    // The authoring order IS the correct order; the taker shuffles for display.
    const commit = (next: typeof items) =>
      update(index, { items: next, correctOrder: next.map((i) => i.id) });
    const move = (from: number, to: number) => {
      if (to < 0 || to >= items.length) return;
      const next = [...items];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      commit(next);
    };
    return (
      <Stack spacing={1}>
        <Typography variant="caption" color="text.secondary">
          {t('assignmentsAdmin.quiz.orderingHelp')}
        </Typography>
        {items.map((it, i) => (
          <Stack key={it.id} direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" sx={{ width: 20, textAlign: 'right' }}>
              {i + 1}.
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={t('assignmentsAdmin.quiz.itemText')}
              value={it.text}
              onChange={(e) =>
                commit(items.map((x) => (x.id === it.id ? { ...x, text: e.target.value } : x)))
              }
            />
            <IconButton size="small" onClick={() => move(i, i - 1)} disabled={i === 0}>
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => move(i, i + 1)}
              disabled={i === items.length - 1}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={items.length <= 2}
              onClick={() => commit(items.filter((x) => x.id !== it.id))}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => commit([...items, { id: newId(), text: '' }])}
        >
          {t('assignmentsAdmin.quiz.addItem')}
        </Button>
      </Stack>
    );
  }

  if (question.kind === 'numeric') {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          type="number"
          label={t('assignmentsAdmin.quiz.correctValue')}
          value={question.value}
          onChange={(e) => update(index, { value: Number(e.target.value) })}
          sx={{ width: 140 }}
        />
        <TextField
          size="small"
          type="number"
          label={t('assignmentsAdmin.quiz.tolerance')}
          value={question.tolerance}
          onChange={(e) => update(index, { tolerance: Math.max(0, Number(e.target.value)) })}
          sx={{ width: 140 }}
        />
        <TextField
          size="small"
          label={t('assignmentsAdmin.quiz.unit')}
          value={question.unit ?? ''}
          onChange={(e) => update(index, { unit: e.target.value })}
          sx={{ width: 140 }}
        />
      </Stack>
    );
  }

  if (question.kind === 'cloze') {
    const blankIds = parseBlankIds(question.template);
    const blanks = question.blanks;
    const setBlank = (blankId: string, acceptedAnswers: string[]) =>
      update(index, {
        blanks: { ...blanks, [blankId]: { acceptedAnswers, normalization: DEFAULT_NORM } },
      });
    return (
      <Stack spacing={1.5}>
        <TextField
          fullWidth
          size="small"
          label={t('assignmentsAdmin.quiz.template')}
          helperText={t('assignmentsAdmin.quiz.templateHelp')}
          value={question.template}
          multiline
          minRows={2}
          onChange={(e) => update(index, { template: e.target.value })}
        />
        {blankIds.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            {t('assignmentsAdmin.quiz.clozeNoBlanks')}
          </Typography>
        )}
        {blankIds.map((blankId) => (
          <TextField
            key={blankId}
            fullWidth
            size="small"
            label={t('assignmentsAdmin.quiz.blankAccepted', { n: blankId })}
            helperText={t('assignmentsAdmin.quiz.acceptedAnswersHelp')}
            value={(blanks[blankId]?.acceptedAnswers ?? []).join('\n')}
            multiline
            minRows={1}
            onChange={(e) => setBlank(blankId, e.target.value.split('\n'))}
          />
        ))}
      </Stack>
    );
  }

  if (question.kind === 'open') {
    return (
      <TextField
        fullWidth
        size="small"
        required
        label={t('assignmentsAdmin.quiz.openStandard')}
        helperText={t('assignmentsAdmin.quiz.openStandardHelp')}
        value={question.standard ?? ''}
        multiline
        minRows={3}
        error={!(question.standard ?? '').trim()}
        onChange={(e) => update(index, { standard: e.target.value })}
      />
    );
  }

  if (question.kind !== 'short_answer') return <></>;

  // short_answer
  const answers = question.acceptedAnswers;
  const norm = question.normalization ?? { trim: true, lowercase: true, stripAccents: true };
  return (
    <Stack spacing={1}>
      <TextField
        fullWidth
        size="small"
        label={t('assignmentsAdmin.quiz.acceptedAnswers')}
        helperText={t('assignmentsAdmin.quiz.acceptedAnswersHelp')}
        value={answers.join('\n')}
        multiline
        minRows={2}
        onChange={(e) => update(index, { acceptedAnswers: e.target.value.split('\n') })}
      />
      <Stack direction="row" spacing={2}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={norm.lowercase}
              onChange={(e) =>
                update(index, { normalization: { ...norm, lowercase: e.target.checked } })
              }
            />
          }
          label={t('assignmentsAdmin.quiz.ignoreCase')}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={norm.stripAccents}
              onChange={(e) =>
                update(index, { normalization: { ...norm, stripAccents: e.target.checked } })
              }
            />
          }
          label={t('assignmentsAdmin.quiz.ignoreAccents')}
        />
      </Stack>
    </Stack>
  );
}
